import { CityProfile, ICityProfile } from '@app/models/CityProfile'
import { NationalPlace } from '@app/models/NationalPlace'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ICityEditState {
  readonly canSave: boolean
  readonly city: ICityProfile
}

const StateRecord = Record({
  canSave: false,
  city: undefined,
})

export class CityEditState extends StateRecord implements ICityEditState {
  public static fromJS(initialState?: Partial<ICityEditState>) {
    return new CityEditState(initialState)
  }

  public readonly canSave: boolean
  public readonly city: CityProfile

  constructor(props?: Partial<ICityEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadCity = 'CITY_EDIT::LOAD_CITY',
  LoadCitySuccess = 'CITY_EDIT::LOAD_CITY_SUCCESS',
  ResetState = 'CITY_EDIT::RESET_STATE',
  SaveCity = 'CITY_EDIT::SAVE_CITY',
  SaveCityFailure = 'CITY_EDIT::SAVE_CITY_FAILURE',
  SaveCitySuccess = 'CITY_EDIT::SAVE_CITY_SUCCESS',
  SetOverview = 'CITY_EDIT::SET_OVERVIEW',
  SetPlace = 'CITY_EDIT::SET_PLACE',
}

interface ILoadCityAction {
  type: ActionTypes.LoadCity
  value?: string
}

interface ILoadCitySuccessAction {
  type: ActionTypes.LoadCitySuccess
  value: CityProfile
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveCityAction {
  type: ActionTypes.SaveCity
  value?: string
}

interface ISaveCityFailureAction {
  type: ActionTypes.SaveCityFailure
  error: string
}

interface ISaveCitySuccessAction {
  type: ActionTypes.SaveCitySuccess
}

interface ISetOverviewAction {
  type: ActionTypes.SetOverview
  value: string
}

interface ISetPlaceAction {
  type: ActionTypes.SetPlace
  value?: NationalPlace
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadCityAction
  | ILoadCitySuccessAction
  | IResetStateAction
  | ISaveCityAction
  | ISaveCityFailureAction
  | ISaveCitySuccessAction
  | ISetOverviewAction
  | ISetPlaceAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadCity(value?: string): ILoadCityAction {
  return { type: ActionTypes.LoadCity, value }
}

function loadCitySuccess(value: CityProfile): ILoadCitySuccessAction {
  return {
    type: ActionTypes.LoadCitySuccess,
    value,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function save(value?: string): ISaveCityAction {
  return {
    type: ActionTypes.SaveCity,
    value,
  }
}

function saveFailure(error: string): ISaveCityFailureAction {
  return {
    error,
    type: ActionTypes.SaveCityFailure,
  }
}

function saveSuccess(): ISaveCitySuccessAction {
  return {
    type: ActionTypes.SaveCitySuccess,
  }
}

function setOverview(value: string): ISetOverviewAction {
  return {
    type: ActionTypes.SetOverview,
    value,
  }
}

function setPlace(value?: NationalPlace): ISetPlaceAction {
  return {
    type: ActionTypes.SetPlace,
    value,
  }
}

export const actionCreators = {
  loadCity,
  loadCitySuccess,
  resetState,
  save,
  setOverview,
  setPlace,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<CityEditState> = (
  state: CityEditState = new CityEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.LoadCity:
      if (!action.value) {
        const newCity = new CityProfile()
        return state.set('city', newCity) as CityEditState
      } else {
        return state as CityEditState
      }

    case ActionTypes.LoadCitySuccess:
      return state
        .set('city', action.value)
        .set('canSave', true) as CityEditState

    case ActionTypes.ResetState:
      return state.set('canSave', false).set('city', undefined) as CityEditState

    case ActionTypes.SaveCity:
      return state as CityEditState

    case ActionTypes.SaveCityFailure:
      return state as CityEditState

    case ActionTypes.SaveCitySuccess:
      return state as CityEditState

    case ActionTypes.SetOverview:
      return state.setIn(['city', 'overview'], action.value) as CityEditState

    case ActionTypes.SetPlace:
      return state.setIn(['city', 'place'], action.value) as CityEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadCitySaga(action: ILoadCityAction) {
  try {
    if (action.value) {
      const baseUrl = `/api/cityProfiles/${action.value}`

      const result = yield makeAuthenticatedRequest(baseUrl)

      if (result.success === true) {
        yield put(resetState())
        yield put(loadCitySuccess(CityProfile.fromJS(result.entity)))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* saveCitySaga(action: ISaveCityAction) {
  try {
    const state = yield select(selectModuleState())

    const baseUrl = action.value
      ? `/api/cityProfiles/${action.value}`
      : '/api/cityProfiles'

    const attachments = yield select(selectAttachments('city-attachments'))
    const photos = yield select(selectAttachments('city-photos'))
    const chamber = yield select(selectAttachments('city-chamber-of-commerce'))
    const brokers = yield select(selectAttachments('city-top-brokers'))
    const trafficCount = yield select(
      selectAttachments('city-traffic-count-maps'),
    )
    const competitor = yield select(selectAttachments('city-competitor-maps'))
    const regional = yield select(
      selectAttachments('city-regional-retail-maps'),
    )
    const retail = yield select(selectAttachments('city-retail-nodemaps'))

    const city = state.city.toJS()
    city.attachments = attachments
    city.photos = photos
    city.chamberOfCommerceFiles = chamber
    city.topBrokers = brokers
    city.trafficCountMaps = trafficCount
    city.competitorsMaps = competitor
    city.regionalMaps = regional
    city.retailNodeMaps = retail

    const clientOpts = {
      body: JSON.stringify(city),
      method: action.value ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, clientOpts)

    if (result.success === true) {
      yield put(saveSuccess())
      yield put(resetState())
      yield put(
        successToast(
          'Success',
          action.value ? `City information updated` : 'New City Created',
        ),
      )
      yield put(push(`/development/city/${result.entity.slug}`))
    } else {
      yield put(saveFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadCity() {
  yield takeLatest(ActionTypes.LoadCity, loadCitySaga)
}

function* watchSaveCity() {
  yield takeLatest(ActionTypes.SaveCity, saveCitySaga)
}

export function* cityEditRootSaga() {
  yield fork(watchLoadCity)
  yield fork(watchSaveCity)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): CityEditState =>
  state.cityEdit
const selectFileUploadState = (state: ApplicationState) => state.fileUpload

export const selectAttachments = (key: string) =>
  createSelector(selectFileUploadState, (mState) => {
    return mState.files
      .get(key)
      .map((item: any) => item.attachment.toJS())
      .toArray()
  })

export const selectCanSave = () =>
  createSelector(selectModuleState(), (mState) => mState.canSave)

export const selectCity = () =>
  createSelector(selectModuleState(), (mState) => mState.city)
